import { useEffect, lazy, Suspense } from "react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

// Eagerly loaded — critical public pages
import HomePage from "@/pages/home";
import NotFound from "@/pages/not-found";

// Lazy loaded — public pages (loaded on navigation)
const CompanionPage = lazy(() => import("@/pages/companion"));
const AvailabilityPage = lazy(() => import("@/pages/availability"));
const ServicesPage = lazy(() => import("@/pages/services"));
const SafetyPage = lazy(() => import("@/pages/safety"));
const TrackingPage = lazy(() => import("@/pages/tracking"));
const LocationPage = lazy(() => import("@/pages/location"));
const LocationsIndexPage = lazy(() => import("@/pages/locations-index"));
const CategoryPage = lazy(() => import("@/pages/category"));
const CategoriesIndexPage = lazy(() => import("@/pages/categories-index"));
const BlogIndexPage = lazy(() => import("@/pages/blog"));
const BlogPostPage = lazy(() => import("@/pages/blog-post"));
const ServiceTypePage = lazy(() => import("@/pages/service-type"));
const FAQPage = lazy(() => import("@/pages/faq"));
const ReviewsPage = lazy(() => import("@/pages/reviews"));
const AboutPage = lazy(() => import("@/pages/about"));
const ContactPage = lazy(() => import("@/pages/contact"));
const BookingPage = lazy(() => import("@/pages/booking-page"));
const RecruitmentPage = lazy(() => import("@/pages/recruitment"));
const PrivacyPolicyPage = lazy(() => import("@/pages/privacy-policy"));
const TermsPage = lazy(() => import("@/pages/terms"));

// Lazy loaded — admin/portal pages (never needed on public homepage)
const AdminPage = lazy(() => import("@/pages/admin"));
const AdminInboxPage = lazy(() => import("@/pages/admin/inbox"));
const SEOEnginePage = lazy(() => import("@/pages/admin/seo-engine"));
const SocialHubPage = lazy(() => import("@/pages/admin/social-hub"));
const ContentStudioPage = lazy(() => import("@/pages/admin/content-studio"));
const CompanionPortal = lazy(() => import("@/pages/portal/companion"));
const DriverPortal = lazy(() => import("@/pages/portal/driver"));
const HostessPortal = lazy(() => import("@/pages/portal/hostess"));

function Router() {
  return (
    <Switch>
      <Route path="/" component={HomePage} />
      <Route path="/companion/:id" component={CompanionPage} />
      <Route path="/availability" component={AvailabilityPage} />
      <Route path="/services" component={ServicesPage} />
      <Route path="/services/:type" component={ServiceTypePage} />
      <Route path="/safety" component={SafetyPage} />
      <Route path="/tracking/:code" component={TrackingPage} />
      <Route path="/locations" component={LocationsIndexPage} />
      <Route path="/location/:slug" component={LocationPage} />
      <Route path="/categories" component={CategoriesIndexPage} />
      <Route path="/categories/:slug" component={CategoryPage} />
      <Route path="/blog" component={BlogIndexPage} />
      <Route path="/blog/:slug" component={BlogPostPage} />
      <Route path="/faq" component={FAQPage} />
      <Route path="/reviews" component={ReviewsPage} />
      <Route path="/about" component={AboutPage} />
      <Route path="/contact" component={ContactPage} />
      <Route path="/booking" component={BookingPage} />
      <Route path="/recruitment" component={RecruitmentPage} />
      <Route path="/privacy-policy" component={PrivacyPolicyPage} />
      <Route path="/terms" component={TermsPage} />
      <Route path="/portal/companion" component={CompanionPortal} />
      <Route path="/portal/driver" component={DriverPortal} />
      <Route path="/portal/hostess" component={HostessPortal} />
      <Route path="/admin/content-studio" component={ContentStudioPage} />
      <Route path="/admin/social-hub" component={SocialHubPage} />
      <Route path="/admin/seo-engine" component={SEOEnginePage} />
      <Route path="/admin/inbox" component={AdminInboxPage} />
      <Route path="/admin" component={AdminPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  useEffect(() => {
    // Prevent right-click on images
    document.addEventListener("contextmenu", (e) => {
      if ((e.target as HTMLElement).tagName === "IMG") {
        e.preventDefault();
      }
    });

    // Warn on print/screenshot attempt
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "PrintScreen") {
        e.preventDefault();
        document.body.style.filter = "blur(20px)";
        setTimeout(() => { document.body.style.filter = ""; }, 1500);
      }
      if (e.ctrlKey && e.key === "p") {
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Suspense fallback={<div className="min-h-screen bg-[#0F0F0F]" />}>
          <Router />
        </Suspense>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
